import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import { createTeacherSupportRequest } from "../services/APIClient";
import {
  buildTeacherSupportRequestPayload,
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherOfferMeetingModal.css";

const topics = [
  { value: "Understanding results", helper: "Help interpreting assessment results" },
  { value: "Class setup", helper: "Help creating or fixing classes" },
  { value: "Export issue", helper: "Help with Aladdin or printouts" },
  { value: "Account setup", helper: "Help getting teachers started" },
  { value: "Other", helper: "Something else" }
];

const guideByTopic = {
  "Understanding results": {
    title: "Results guide",
    query: "understanding results",
    type: "iframe"
  },
  "Class setup": {
    title: "Class setup guide",
    query: "class setup",
    type: "iframe"
  },
  "Export issue": {
    title: "Aladdin export guide",
    query: "export results",
    type: "pdf"
  },
  "Account setup": {
    title: "Teacher account setup guide",
    query: "teacher account setup",
    type: "pdf"
  },
  Other: {
    title: "ALPACA support guidance",
    query: "support guidance",
    type: "iframe"
  }
};

const MEETING_BUFFER_HOURS = 24;

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getMinMeetingDate = () =>
  formatLocalDate(new Date(Date.now() + MEETING_BUFFER_HOURS * 60 * 60 * 1000));

const getMinMeetingTimeForDate = (date) => {
  if (!date) return "09:00";
  const minDateTime = new Date(Date.now() + MEETING_BUFFER_HOURS * 60 * 60 * 1000);
  if (date !== formatLocalDate(minDateTime)) return "09:00";
  const minMinutes = minDateTime.getHours() * 60 + minDateTime.getMinutes();
  const roundedMinutes = Math.ceil(minMinutes / 15) * 15;
  const clampedMinutes = Math.min(Math.max(roundedMinutes, 9 * 60), 17 * 60);
  return `${String(Math.floor(clampedMinutes / 60)).padStart(2, "0")}:${String(clampedMinutes % 60).padStart(2, "0")}`;
};

const isValidMeetingDateTime = (date, time) => {
  if (!date || !time) return false;
  const [hourValue, minuteValue] = String(time).split(":").map(Number);
  if (!Number.isInteger(hourValue) || !Number.isInteger(minuteValue)) return false;
  const totalMinutes = hourValue * 60 + minuteValue;
  if (totalMinutes < 9 * 60 || totalMinutes > 17 * 60 || totalMinutes % 15 !== 0) return false;
  const [year, month, day] = date.split("-").map(Number);
  const selected = new Date(year, month - 1, day, hourValue, minuteValue, 0, 0);
  return selected >= new Date(Date.now() + MEETING_BUFFER_HOURS * 60 * 60 * 1000);
};

function TeacherOfferMeetingModal({
  show,
  handleClose,
  prefill = null,
  schoolid = null,
  teacherName = "",
  teacherEmail = "",
  onOpenGuidance = null,
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [meetingTopic, setMeetingTopic] = useState("");
  const [note, setNote] = useState("");
  const [requestedDate, setRequestedDate] = useState("");
  const [requestedTime, setRequestedTime] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);

  useEffect(() => {
    if (!show) return;
    setStage("form");
    setMeetingTopic(prefill?.meetingTopic || "");
    setNote(prefill?.note || "");
    setRequestedDate(prefill?.requestedDate || "");
    setRequestedTime(prefill?.requestedTime || "");
    setError("");
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);
  }, [show, prefill]);

  const suggestedGuide = guideByTopic[meetingTopic] || null;
  const canContinue =
    meetingTopic &&
    note.trim() &&
    isValidMeetingDateTime(requestedDate, requestedTime);
  const topicLabel = meetingTopic.toLowerCase();

  const handleNext = () => {
    if (!canContinue) return;

    if (suggestedGuide) {
      setStage("guide");
      return;
    }

    sendMeetingRequest();
  };

  const openSuggestedGuide = () => {
    if (onOpenGuidance && suggestedGuide) {
      onOpenGuidance({
        guidanceQuery: suggestedGuide.query
      });
      return;
    }

    handleClose();
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message: "Meeting request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const returnToTeacherDashboard = () => {
    if (embedded) {
      handleDone();
      return;
    }

    if (onReturnToTeacherDashboard) {
      onReturnToTeacherDashboard();
      return;
    }

    handleClose();
  };

  const sendMeetingRequest = async () => {
    if (!canContinue || isSubmitting) return;
    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const payload = buildTeacherSupportRequestPayload({
      type: "meeting_request",
      schoolId: schoolid,
      details: {
        meetingTopic,
        note,
        requestedDate,
        requestedTime,
        schoolName: prefill?.schoolName,
        teacherName: prefill?.teacherName || teacherName,
        email: prefill?.email || teacherEmail
      },
      source: {
        workflow: "offer-meeting"
      }
    });

    try {
      const response = await createTeacherSupportRequest(payload);
      setSupportReference(getSupportRequestReference(response));
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-offer-meeting-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Offer Meeting</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="offer-meeting-intro">Choose what you need help with and pick a date and time.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="offer-meeting-section">
              <Form.Label>What is the meeting about?</Form.Label>
              <div className="offer-meeting-options">
                {topics.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`offer-meeting-option${meetingTopic === option.value ? " selected" : ""}`}
                    onClick={() => setMeetingTopic(option.value)}
                  >
                    <span className="offer-meeting-radio" aria-hidden="true" />
                    <span>
                      <strong>{option.value}</strong>
                      <span>{option.helper}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Form.Group>
            <Form.Group className="offer-meeting-section" controlId="meetingNote">
              <Form.Label>Short context</Form.Label>
              <Form.Text as="div" className="offer-meeting-field-label">
                What would you like to cover?
              </Form.Text>
              <Form.Control value={note} onChange={(event) => setNote(event.target.value)} />
            </Form.Group>
            <Form.Group className="offer-meeting-section">
              <Form.Label>Choose a date and time</Form.Label>
              <Form.Group className="mb-3" controlId="meetingDate">
                <Form.Label className="offer-meeting-field-label">Date</Form.Label>
                <Form.Control type="date" min={getMinMeetingDate()} value={requestedDate} onChange={(event) => setRequestedDate(event.target.value)} />
              </Form.Group>
              <Form.Group controlId="meetingTime">
                <Form.Label className="offer-meeting-field-label">Time</Form.Label>
                <Form.Control type="time" min={getMinMeetingTimeForDate(requestedDate)} max="17:00" step="900" value={requestedTime} onChange={(event) => setRequestedTime(event.target.value)} />
              </Form.Group>
              <Form.Text className="text-muted">Choose a 15-minute slot between 09:00 and 17:00, at least 24 hours from now.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <span className="offer-meeting-footer-note">{canContinue ? "Meeting details ready." : "Choose a valid date, 15-minute time, and context."}</span>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={handleNext} disabled={!canContinue || isSubmitting}>
              {isSubmitting ? "Sending..." : "Next"}
            </Button>
          </Modal.Footer>
        </>
      )}
      {stage === "guide" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Try A Short Guide First</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="offer-meeting-intro">This may help before the meeting.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="offer-meeting-warning">
              <strong>{suggestedGuide?.title || "Guide available"}</strong>
              <span>Open this resource now, or request a meeting if you still want to speak with someone.</span>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <span className="offer-meeting-footer-note">Guide offered.</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
            <Button variant="outline-primary" onClick={openSuggestedGuide}>Open guide</Button>
            <Button variant="primary" onClick={sendMeetingRequest} disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Request meeting anyway"}
            </Button>
          </Modal.Footer>
        </>
      )}
      {stage === "success" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Meeting Requested</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="offer-meeting-intro">Support will follow up about your {topicLabel} meeting.</p>
            <Alert variant="success" className="offer-meeting-success">
              <span className="offer-meeting-ok" aria-hidden="true"><FaCheckCircle /></span>
              <span>
                <strong>
                  {isDuplicatePending
                    ? "Your meeting request is already in progress."
                    : "Your meeting request has been sent."}
                </strong>
                <span>
                  {supportReference ? `Reference: ${supportReference}. ` : ""}
                  Support will follow up about your {topicLabel} meeting.
                </span>
              </span>
            </Alert>
            <div className="offer-meeting-next">
              <strong>What next?</strong>
              <span>{embedded ? "Close back to SENSAI" : "Return to Teacher Dashboard"}, or open help guidance if you want a resource while you wait.</span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="offer-meeting-footer-note">Task complete.</span>
            <Button variant="outline-secondary" onClick={openSuggestedGuide}>Open guidance</Button>
            {!embedded && (
              <Button variant="outline-secondary" onClick={returnToTeacherDashboard}>Return to Teacher Dashboard</Button>
            )}
            <Button variant="primary" onClick={handleDone}>Close</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherOfferMeetingModal;
